import { AngleDownIconComponent } from '../icons/angle-down-icon.component';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { BaseComponent } from '../../../base/components/base.component';
import { USER_KEY } from '../../service/storage.service';
import { User, UserCategory, UserType } from '../../models/user';
import { CommonModule } from '@angular/common';
import {
  ActivatedRoute,
  Router,
  RouterModule,
} from '@angular/router';
import { ClickOutsideDirective } from '../../utils/click.outside.directive';
import { SecondaryRoles } from '../../interfaces/secondary-roles';
import { ProductService } from '../../service/product.service';
import { UserInfoData } from '../../models/user-info-response';
import { SidebarService } from '../../service/sidebar.service';
import { ClickableDirective } from '../../directives/clickable.directive';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-header',
    imports: [
      CommonModule,
      ClickOutsideDirective,
      RouterModule,
      ClickableDirective,
      AngleDownIconComponent
    ],
    templateUrl: './header.component.html',
    styleUrl: './header.component.scss'
})
export class HeaderComponent extends BaseComponent implements OnInit, OnDestroy {
  route = inject(ActivatedRoute);
  user: User = {} as User;
  userType = SecondaryRoles;
  productService = inject(ProductService);
  loggedUser: string = '';
  userInfo?: UserInfoData;
  userCategory = UserCategory;
  path = inject(Router);
  fullPath!: string;
  private authUserSubscription?: Subscription;

  constructor() {
    super();

    this.user = this.storageService.getObject(USER_KEY) || ({} as User);
    this.fullPath = this.path.url;
  }
  
  userRole = UserType;
  dropdownOpen = false;

  sidebar = inject(SidebarService);
  onToggleSidebar() {
    this.sidebar.toggleSidebar();
  }

  logout() {
    if (this.user?.id) {
      this.spinner.show();
      this.authService.logout(false).subscribe({
        next: (authResponse) => {
          this.spinner.hide();
          this.productService.clean();
          this.storageService.clean();
          this.router.navigate(['/login']);
        },
        error: (err) => {
          this.logger.error(err);
          this.router.navigate(['/login']);
          this.spinner.hide();
        },
        complete: () => {
          this.spinner.hide();
        },
      });
    } else {
      this.productService.clean();
      this.storageService.clean();
      this.router.navigate(['/login']);
    }
  }

  secondaryRoles: any = [];

  private rebuildSecondaryRoles() { 
    this.secondaryRoles = [];

    const secondaryRoles = Array.isArray(this.user?.secondryRoles)
      ? [...this.user.secondryRoles]
      : [];

    if (typeof this.user?.user_type === 'number') {
      secondaryRoles.unshift(this.user.user_type);
    }

    const uniqueRoles = secondaryRoles.filter(
      (role, index, list) => list.indexOf(role) === index,
    );

    uniqueRoles.forEach((element: number) => {
      let role_route = { val: '', link: '' };
      switch (element) {
        case this.userType.CUSTOMER:
          role_route = { val: 'Customer', link: 'customer' };
          this.secondaryRoles.push(role_route);
          break;
        case this.userType.SYSTEM_INTEGRATOR:
          role_route = { val: 'System Integrator', link: 'system-integrator' };
          this.secondaryRoles.push(role_route);
          break;
        case this.userType.ADMIN:
          role_route = { val: 'Admin', link: 'customer/dashboard' };
          this.secondaryRoles.push(role_route);
          break;
        default:
          break;
      }
    });
  }

  ngOnInit(): void {
    this.rebuildSecondaryRoles();

    this.authUserSubscription = this.authService.authenticatedUser.subscribe(
      (authenticatedUser) => {
        if (!authenticatedUser) return;
        this.user = authenticatedUser;
        this.rebuildSecondaryRoles();
      },
    );

    this.router.events.subscribe(() => {
      this.fullPath = this.path.url;
    });
  }

  ngOnDestroy(): void {
    this.authUserSubscription?.unsubscribe();
  }

  fetchUserProfile() {
    this.authService.fetchUserInfo(this.user.id).subscribe({
      next: (infoResponse) => {
        if (infoResponse.status == 200 || infoResponse.status == 204) {
          this.userInfo = infoResponse.data;
          this.user.email = this.userInfo.email;
          this.user.phone = this.userInfo.phone;
          this.user.user_image = this.userInfo.user_image;

          this.storageService.setObject(USER_KEY, this.user);
        }
      },
      error: (err) => {
        this.logger.error(err);
      },
      complete: () => { },
    });
  }

  switchUserType(event: Event) {
    let val = (event.target as HTMLInputElement).value;
    switch (val) {
      case 'customer/dashboard':
        this.router.navigate(['/dashboard', 'customer-dashboard']);
        break;
      case 'si/dashboard':
        this.router.navigate(['/si', 'home']);
        break;

      default:
        break;
    }
  }

  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
  }

  clickOutside() {
    this.dropdownOpen = false;
  }
  isLargeScaleUser(): boolean {
  return this.user?.primary_role === 'LARGE_PLANT_USER';
}
}
